import {
  Checkbox,
  createStyles,
  FormControlLabel,
  Link,
  makeStyles,
  Typography,
} from "@material-ui/core"
import clsx from "clsx"
import { useEffect, useState } from "react"
import { useLocation, useParams } from "react-router-dom"
import { GraphQLClient } from "graphql-request"
import * as EmailValidator from "email-validator"
import emojiRegex from "emoji-regex"
import {
  ActivateAccountDocument,
  ActivateAccountMutation,
  ActivateAccountMutationVariables,
  InsertInviteCodeDocument,
  InsertInviteCodeMutation,
  InsertInviteCodeMutationVariables,
  OnboardUserDocument,
  OnboardUserMutation,
  OnboardUserMutationVariables,
  UpdateInviteCodeDocument,
  UpdateInviteCodeMutation,
  UpdateInviteCodeMutationVariables,
  UpdateInviteCodeStatusDocument,
  UpdateInviteCodeStatusMutation,
  UpdateInviteCodeStatusMutationVariables,
} from "../../generated/graphql"
import { useNavigation } from "../../hooks/useNavigation"
import { useApi } from "../../utils/API"
import { colors } from "../../utils/theme"
import { PrimaryButton } from "../atoms/button/Button"
import Input from "../atoms/input/Input"
import Logo from "../atoms/Logo"
import PasswordRules from "../molecules/PasswordRules"
import { TitledCard } from "../molecules/titledCard/TitledCard"
import { BaseTemplate } from "../templates/BaseTemplate"
import { InviteCodeStatus } from "../../utils/constants"
import { ENDPOINTS } from "../../utils/endpoints"

const useStyles = makeStyles((theme) =>
  createStyles({
    register: {
      display: "flex",
      flex: 1,
      flexDirection: "column",
      height: "100vh",
      backgroundColor: colors.primary.cloud,
      alignItems: "center",
      justifyContent: "center",
    },
    button: {
      width: "100%",
    },
    marginBottom16: {
      marginBottom: theme.spacing(2),
    },
    marginBottom8: {
      marginBottom: theme.spacing(1),
    },
    card: {
      width: 440,
      "& [class^=makeStyles-card-]": {
        width: "440px !important",
      },
    },
    registerContainer: {
      display: "flex",
      alignItems: "center",
      alignSelf: "center",
    },
    logo: {
      position: "absolute",
      top: theme.spacing(6),
      left: theme.spacing(6),
    },
    center: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    input: {
      width: "100%",
      marginBottom: theme.spacing(2),
    },
    login: {
      color: colors.neutral.asphalt,
      marginRight: 4,
    },
    subtitle: {
      fontWeight: 400,
      color: colors.neutral.denim,
      marginBottom: theme.spacing(4),
    },
    inputContainer: {
      position: "relative",
      width: "100%",
    },
    marginBottom32: {
      marginBottom: theme.spacing(4),
    },
    denim: {
      color: colors.neutral.denim,
    },
    crimson: {
      color: colors.secondary.crimson,
    },
    periwinkle: {
      color: colors.primary.periwinkle,
    },
  })
)

interface Params {
  code: string
}

const ClientInvitation = () => {
  const classes = useStyles()
  const API = useApi()
  const [email, setEmail] = useState<string>("")
  const [code, setCode] = useState<string>("")
  const [firstName, setFirstName] = useState<string>("")
  const [lastName, setLastName] = useState<string>("")
  const [password, setPassword] = useState<string>("")
  const [tos, setTos] = useState<boolean>(false)
  const [emailError, setEmailError] = useState<string | undefined>(undefined)
  const [reenteredPassword, setReenteredPassword] = useState<string>("")
  const [passwordError, setPasswordError] = useState<string | undefined>(
    undefined
  )
  const [reenteredPasswordError, setReenteredPasswordError] = useState<
    string | undefined
  >(undefined)
  const [firstNameError, setFirstNameError] = useState<string | undefined>(
    undefined
  )
  const [lastNameError, setLastNameError] = useState<string | undefined>(
    undefined
  )
  const [tosError, setTosError] = useState<boolean>(false)

  const [passwordFocus, setPasswordFocus] = useState<boolean>(false)
  const [reenteredPasswordFocus, setReenteredPasswordFocus] =
    useState<boolean>(false)
  const [validPassword, setValidPassword] = useState<boolean>(false)
  const [validCode, setValidCode] = useState<boolean>(true)
  const params: Params = useParams()
  const location = useLocation()
  const queryParameters = new URLSearchParams(location.search)
  const inviteCode = queryParameters.get("id")
  const [org, setOrg] = useState<string>("")
  const [practitionerId, setPractitionerId] = useState<string>("")

  const { toClientInvitationSuccess } = useNavigation()

  const handleTosChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTos(event.target.checked)
  }

  function hasEmojis(value: string) {
    const regex = emojiRegex()
    return regex.test(value)
  }

  useEffect(() => {
    if (inviteCode && inviteCode.length > 0) {
      setCode(inviteCode)

      const getInviteDetails = async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_VIRA_XEALTH_APIM_ENDPOINT}/code`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ code: inviteCode.toUpperCase() }),
            }
          )
          const data = await response.json()

          if (response.status === 200) {
            const { organization_members } = data
            const orgName =
              organization_members.length > 0
                ? `${organization_members[0].organization.name}`
                : ""
            const id =
              organization_members.length > 0
                ? organization_members[0].practitioner.id
                : ""
            setPractitionerId(id)
            setEmail(data.email)
            setOrg(orgName)
            setValidCode(data.isValid)
          } else {
            setValidCode(false)
          }
        } catch (error) {
          setValidCode(false)
        }
      }
      getInviteDetails()
    } else {
      setValidCode(false)
    }
  }, [params])

  const submit = async () => {
    setPasswordError(undefined)
    setReenteredPasswordError(undefined)
    setFirstNameError(undefined)
    setLastNameError(undefined)
    setEmailError(undefined)
    setTosError(false)
    const errors = {
      firstName: false,
      lastName: false,
      password: false,
      reenteredPassword: false,
      tos: false,
      email: false,
    }
    if (firstName === "") {
      errors.firstName = true
      setFirstNameError("You need to enter a first name to continue.")
    }
    const firstNameHasEmojis = hasEmojis(firstName)
    if (firstNameHasEmojis) {
      errors.firstName = true
      setFirstNameError("Please enter your first name without emojis")
    }
    if (lastName === "") {
      errors.lastName = true
      setLastNameError("You need to enter a last name to continue.")
    }
    const lastNameHasEmojis = hasEmojis(firstName)
    if (lastNameHasEmojis) {
      errors.lastName = true
      setLastNameError("Please enter your last name without emojis")
    }
    if (password === "") {
      errors.password = true
      setPasswordError("You need to enter a valid password to continue.")
    }
    if (tos === false) {
      errors.tos = true
      setTosError(true)
    }
    if (email === "") {
      errors.email = true
      setEmailError("You need to enter an email address to continue.")
    }
    if (email !== "" && !EmailValidator.validate(email)) {
      errors.email = true
      setEmailError("Please enter a valid email address to continue.")
    }
    if (reenteredPassword === "") {
      errors.reenteredPassword = true
      setReenteredPasswordError(
        "You need to re-enter your password to continue."
      )
    } else if (password !== reenteredPassword) {
      errors.password = true
      errors.reenteredPassword = true
      setReenteredPasswordError("The passwords you enter must match.")
    } else if (!validPassword) {
      errors.password = true
      setPasswordError("Password does not meet the requirements.")
    }
    if (
      errors.firstName ||
      errors.lastName ||
      errors.password ||
      errors.reenteredPassword ||
      errors.tos ||
      errors.email
    ) {
      return
    }
    try {
      // register client
      const response = await API.registerClient(email, password, {
        first_name: firstName,
        last_name: lastName,
        notification_time: new Date(),
      })
      if (response.status !== 200) {
        const { error } = response.data
        const { message } = error[0]
        if (message.includes("accounts_email_key")) {
          setEmailError("An account with this email already exists.")
        }
        return
      }

      const userId = response.data?.user.id

      // activate client
      const session = {
        jwt: response.data.jwt_token,
        userId: response.data.user.id,
        role: response.data.user.default_role,
      }

      const client = new GraphQLClient(ENDPOINTS.GRAPHQL, {
        headers: { Authorization: `Bearer ${session.jwt}` },
      })

      await client.request<
        ActivateAccountMutation,
        ActivateAccountMutationVariables
      >(ActivateAccountDocument, {
        email,
      })

      // insert invite code with client_id and code
      await client.request<
        InsertInviteCodeMutation,
        InsertInviteCodeMutationVariables
      >(InsertInviteCodeDocument, {
        client_id: userId,
        code: code.toUpperCase(),
      })

      // set status to pending when connecting with practitioner
      await client.request<
        UpdateInviteCodeMutation,
        UpdateInviteCodeMutationVariables
      >(UpdateInviteCodeDocument, {
        code: code.toUpperCase(),
        practitioner_id: practitionerId,
      })

      // connect practitioner to client
      await client.request<
        UpdateInviteCodeStatusMutation,
        UpdateInviteCodeStatusMutationVariables
      >(UpdateInviteCodeStatusDocument, {
        code: code.toUpperCase(),
        status: InviteCodeStatus.CONNECTED,
      })

      // onboard user in Vira App
      await client.request<OnboardUserMutation, OnboardUserMutationVariables>(
        OnboardUserDocument,
        {
          userId: userId,
        }
      )

      // update code with client_id in ehr.client_invite_codes table
      await fetch(
        `${process.env.REACT_APP_VIRA_XEALTH_APIM_ENDPOINT}/updatecode`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ code: code.toUpperCase(), userId: userId }),
        }
      )

      // Assign default user-level feature flags
      await API.assignDefaultUserFeatureFlags(code, true, {
        headers: { Authorization: `Bearer ${session.jwt}` },
      })

      // Navigate to the client invitation success page
      toClientInvitationSuccess()
    } catch (error) {
      setEmailError((error as Error).message)
    }
  }

  return (
    <>
      <BaseTemplate>
        <div
          onKeyDown={async (event) => {
            if (event.key === "Enter") {
              await submit()
            }
          }}
        >
          <Logo className={classes.logo} />
          {validCode ? (
            <div className={classes.card}>
              <TitledCard title="Welcome to Vira!">
                <Typography variant="body1" className={classes.subtitle}>
                  Your care team at {org} has invited you. Fill out the form
                  below to create your account and get started.
                </Typography>
                <Input
                  label="First Name"
                  placeholder="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={clsx(classes.input)}
                  error={Boolean(firstNameError)}
                  helperText={firstNameError}
                  required={true}
                />
                <Input
                  label="Last Name"
                  placeholder="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={clsx(classes.input)}
                  error={Boolean(lastNameError)}
                  helperText={lastNameError}
                  required={true}
                />
                <Input
                  type="email"
                  autoComplete="email"
                  label="Email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={clsx(classes.input)}
                  contentEditable={false}
                  required={true}
                  error={Boolean(emailError)}
                  helperText={emailError}
                />
                <div className={classes.inputContainer}>
                  <Input
                    onFocus={() => setPasswordFocus(true)}
                    onBlur={() => setPasswordFocus(false)}
                    autoComplete="new-password"
                    type="password"
                    label="Password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={clsx(classes.input)}
                    error={Boolean(passwordError)}
                    helperText={passwordError}
                    enableShowPassword={true}
                    required={true}
                  />
                  {passwordFocus && (
                    <PasswordRules
                      password={password}
                      comparisonPassword={reenteredPassword}
                      setValidPassword={setValidPassword}
                      left={326}
                    />
                  )}
                </div>
                <div className={classes.inputContainer}>
                  <Input
                    onFocus={() => setReenteredPasswordFocus(true)}
                    onBlur={() => setReenteredPasswordFocus(false)}
                    type="password"
                    label="Re-enter Password"
                    placeholder="Re-enter Password"
                    value={reenteredPassword}
                    onChange={(e) => setReenteredPassword(e.target.value)}
                    className={clsx(classes.input)}
                    error={Boolean(reenteredPasswordError)}
                    helperText={reenteredPasswordError}
                    enableShowPassword={true}
                    required={true}
                  />
                  {reenteredPasswordFocus && (
                    <PasswordRules
                      password={password}
                      comparisonPassword={reenteredPassword}
                      setValidPassword={setValidPassword}
                      left={326}
                    />
                  )}
                </div>
                <div className={classes.marginBottom16}>
                  <PrimaryButton onClick={submit} className={classes.button}>
                    Create Account
                  </PrimaryButton>
                </div>
                <FormControlLabel
                  value="end"
                  control={
                    <Checkbox
                      color="primary"
                      name="tos"
                      onChange={handleTosChange}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I have read and agree to the{" "}
                      <Link
                        href="https://externalpartnershare.blob.core.windows.net/public-assets/Vira_Terms_of_Use.pdf"
                        rel="noopener noreferrer"
                        target="_blank"
                        className={classes.periwinkle}
                      >
                        Terms of Use
                      </Link>
                    </Typography>
                  }
                  labelPlacement="end"
                />
                {tosError && (
                  <Typography variant="body2" className={clsx(classes.crimson)}>
                    You must agree to the Terms of Use to continue.
                  </Typography>
                )}
              </TitledCard>
            </div>
          ) : (
            <div className={classes.card}>
              <TitledCard title="Welcome to Vira!">
                <Typography
                  variant="body1"
                  className={clsx(classes.marginBottom32, classes.denim)}
                >
                  Sorry, the link you followed is not valid. Please ask your
                  care team to resend the invitation.
                </Typography>
              </TitledCard>
            </div>
          )}
        </div>
      </BaseTemplate>
    </>
  )
}

export default ClientInvitation
